import React, { useCallback, useEffect, useState } from 'react';
import { 
  Typography, 
  Grid, 
  Card, 
  CardContent, 
  CardActions, 
  Button,
  Box
} from '@mui/material';
import { Link } from 'react-router-dom';
import { agentApi } from '../services/api';
import { Agent } from '../types';
import { useLoading } from '../context/LoadingContext';
import { useErrorHandler } from '../hooks/useErrorHandler';

const Dashboard = () => {
  const [agents, setAgents] = useState<Agent[]>([]);
  const { startLoading, stopLoading } = useLoading();
  const { handleError } = useErrorHandler();

  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const fetchAgents = useCallback(async () => {
    // Prevent multiple simultaneous requests
    if (isLoading) return;
    
    try {
      setIsLoading(true);
      startLoading();
      const data = await agentApi.getAgents();
      setAgents(data);
      setHasError(false);
    } catch (error) {
      handleError(error);
      setHasError(true); // Mark that we had an error
      console.error('Failed to fetch agents:', error);
    } finally {
      setIsLoading(false);
      stopLoading();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handleError]); // Only depend on handleError to avoid infinite loops

  useEffect(() => {
    // Only fetch if we haven't had an error yet
    if (!hasError) {
      fetchAgents();
    }
  }, [fetchAgents, hasError]);
  
  // Add a retry button for when we have errors
  const handleRetry = () => {
    setHasError(false); // Reset error state
    fetchAgents();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Dashboard
        </Typography>
        <Button 
          variant="contained" 
          color="primary" 
          component={Link} 
          to="/agents/create"
        >
          Create New Agent
        </Button>
      </Box>

      <Typography variant="h5" gutterBottom>
        Recent Agents
      </Typography>
      
      {hasError ? (
        <Box sx={{ textAlign: 'center', my: 4 }}>
          <Typography variant="body1" color="error" gutterBottom>
            Failed to load agents. There might be a connection issue with the backend server.
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={handleRetry}
            sx={{ mt: 2 }}
          >
            Retry
          </Button>
        </Box>
      ) : agents.length === 0 ? (
        <Typography variant="body1" color="text.secondary">
          No agents found. Create your first agent to get started.
        </Typography>
      ) : (
        <Grid container spacing={3}>
          {agents.slice(0, 3).map((agent) => (
            <Grid item xs={12} sm={6} md={4} key={agent.id}>
              <Card>
                <CardContent>
                  <Typography variant="h6" component="div">
                    {agent.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Model: {agent.model}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Created: {new Date(agent.createdAt).toLocaleString()}
                  </Typography>
                </CardContent>
                <CardActions>
                  <Button 
                    size="small" 
                    component={Link} 
                    to={`/chat/${agent.id}`}
                  >
                    Chat
                  </Button>
                  <Button 
                    size="small" 
                    component={Link} 
                    to={`/agents/${agent.id}`}
                  >
                    Details
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {agents.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Button 
            component={Link} 
            to="/agents" 
            color="primary"
          >
            View All Agents
          </Button>
        </Box>
      )}

      <Typography variant="h5" sx={{ mt: 4 }} gutterBottom>
        Quick Links
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" component="div">
                Manage Agents
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Create, view, and delete AI agents
              </Typography>
            </CardContent>
            <CardActions>
              <Button 
                size="small" 
                component={Link} 
                to="/agents"
              >
                Go to Agents
              </Button>
            </CardActions>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" component="div">
                Chat Interface
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Interact with your AI agents
              </Typography>
            </CardContent>
            <CardActions>
              <Button 
                size="small" 
                component={Link} 
                to="/chat"
              >
                Go to Chat
              </Button>
            </CardActions>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
